<template>
  <span
    v-focusable="{ attachTabIndex: false }"
    tabindex="-1"
    @focus.capture="onMouseEnterOnTitle"
    @blur.capture="onMouseLeaveFromTitle"
    class="term__wrapper"
    :data-ref="$options.consts.REFS.TERM_WRAPPER"
  >
    <span
      :class="TERM_TITLE_CONTAINER_CLASS"
      @mouseenter="onMouseEnterOnTitle"
      @mouseleave="onMouseLeaveFromTitle"
      ref="termTitle"
    >
      <a-render-html
        v-if="$_hydrationHelpers_isLayoutMobileOrTablet"
        :html="content"
        :container-tag="$options.consts.HTML_TAG.SPAN"
        class="term__term-title"
        @click="showPopup"
      />
      <a-link
        v-else
        :to="termRoute"
        open-in-new-tab
        class="term__term-title ignore-html-styles"
      >
        <a-render-html
          :html="content"
          :container-tag="$options.consts.HTML_TAG.SPAN"
        />
      </a-link>
    </span>
    <fade-transition>
      <span v-if="isPopupVisible" class="term__pop-up-wrapper display-block">
        <span
          class="display-block"
          @mouseenter="onMouseEnterOnPopup"
          @mouseleave="onMouseLeaveFromPopup"
        >
          <a-link
            v-on-clickaway="onTermLinkClickaway"
            class="term__pop-up ignore-html-styles"
            :style="popupStyle"
            :to="termRoute"
            open-in-new-tab
          >
            <i
              v-focusable
              class="close-icon term__close-icon"
              @click.prevent.stop="closePopup"
              @touchend.stop
            ></i>
            <span class="text-body bold display-block ignore-html-styles">
              {{ name }}
            </span>
            <a-dots
              :key="renderKey"
              :text="descriptionText"
              :row-count="3"
              :row-height="$_hydrationHelpers_mobileOrDesktopValue(29, 20)"
              class="term__description"
            />
            <span class="text-caption blue term__link ignore-html-styles"
              >Read this Term</span
            >
          </a-link>
        </span>
      </span>
    </fade-transition>
  </span>
</template>

<script>
import { pipe } from 'ramda'

import { REFS } from '@/utils/enums/external-refs'
import { propValidator, PROP_TYPES } from '@/utils/validators'
import mixins from '@/utils/mixins'
import ADots from 'shared/ADots'
import { directive as onClickaway } from 'vue-clickaway'
import scss from '@/utils/scss'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { processHtml } from '@/utils/helpers/processHtml'
import ARenderHtml from 'shared/ARenderHtml/index.vue'
import { HTML_TAG } from '@fmpedia/enums'
import { hasParentWith } from '@fmpedia/helpers'

const { breakpoints } = scss

const TERM_TITLE_CONTAINER_CLASS = 'term__term-title-container'

/**
 * Be aware that we replaced all DIV tags with SPAN to avoid using div inside p
 * Where: ATerm component is used in article body.
 * If div is rendered inside p, render errors/warnings occur.
 * Bad template: <p>Hello<div>test</div></p>
 */

const POPUP_SETTINGS = {
  MARGIN: 10,
  MIN_WIDTH: 230,
  CLOSE_TIMEOUT: 4000
}

const Z_INDEX = {
  VISIBLE: 1000,
  HIDDEN: 1
}

const DESCRIPTION_MAX_LENGTH = 500

export default {
  name: 'ATerm',
  mixins: [mixins.urlGenerators, hydrationHelpers],
  components: { ADots, ARenderHtml },
  directives: { onClickaway },
  props: {
    content: propValidator([PROP_TYPES.STRING]),
    name: propValidator([PROP_TYPES.STRING]),
    description: propValidator([PROP_TYPES.STRING]),
    slug: propValidator([PROP_TYPES.STRING])
  },
  consts: { REFS, HTML_TAG },
  data() {
    return {
      isPopupVisible: false,
      timeoutId: null,
      renderKey: this.$helper.guid(),
      popupStyle: {
        width: 0,
        right: 0,
        zIndex: this.$_hydrationHelpers_mobileOrDesktopValue(
          Z_INDEX.VISIBLE,
          Z_INDEX.HIDDEN
        )
      },
      TERM_TITLE_CONTAINER_CLASS
    }
  },
  watch: {
    $_hydrationHelpers_windowWidth: {
      immediate: true,
      handler(newVal) {
        if (newVal && process.client) {
          this.$nextTick(this.setPopupStyle)
        }

        if (!this.isPopupVisible) return

        if (newVal > breakpoints.tablet) {
          this.$_hydrationHelpers_showScroll()
        } else {
          this.$_hydrationHelpers_showScroll()
        }
      }
    }
  },
  computed: {
    termRoute() {
      return this.generateTermRoute({ Slug: this.slug || '' })
    },
    descriptionText() {
      const text = pipe(processHtml, this.$helper.htmlToText)(this.description)

      return text.slice(0, DESCRIPTION_MAX_LENGTH)
    }
  },
  methods: {
    onTermLinkClickaway(e) {
      if (
        !this.$_hydrationHelpers_isLayoutMobileOrTablet ||
        hasParentWith(e.target, {
          className: TERM_TITLE_CONTAINER_CLASS
        })
      ) {
        return
      }

      this.closePopup()
    },
    onMouseEnterOnTitle() {
      if (this.$_hydrationHelpers_isLayoutMobileOrTablet) return

      this.popupStyle.zIndex = Z_INDEX.VISIBLE

      this.showPopup()
    },
    onMouseLeaveFromTitle() {
      if (this.$_hydrationHelpers_isLayoutMobileOrTablet) return

      this.closePopupOnTimeout()
    },
    onMouseEnterOnPopup() {
      if (this.$_hydrationHelpers_isLayoutMobileOrTablet) return

      clearTimeout(this.timeoutId)
    },
    onMouseLeaveFromPopup() {
      if (this.$_hydrationHelpers_isLayoutMobileOrTablet) return

      this.closePopupOnTimeout()
    },
    closePopupOnTimeout() {
      this.popupStyle.zIndex = Z_INDEX.HIDDEN

      this.timeoutId = setTimeout(this.closePopup, POPUP_SETTINGS.CLOSE_TIMEOUT)
    },
    showPopup() {
      this.isPopupVisible = true
      this.$nextTick(() => {
        this.renderKey = this.$helper.guid()
      })

      if (this.$_hydrationHelpers_isLayoutMobileOrTablet) {
        this.$_hydrationHelpers_hideScroll()
      }
      clearTimeout(this.timeoutId)
    },
    closePopup() {
      if (!this.isPopupVisible) return

      this.isPopupVisible = false
      if (this.$_hydrationHelpers_isLayoutMobileOrTablet) {
        this.$_hydrationHelpers_showScroll()
      }
    },
    setPopupStyle() {
      this.popupStyle = this.calculatePopupStyle()
    },
    calculatePopupStyle() {
      if (this.$_hydrationHelpers_isLayoutMobileOrTablet) {
        return {
          width: `100vw`
        }
      } else {
        /**
         * We render two article instances wrapped to AVisibility. One of them is
         * hidden and refs are not accessible.
         */
        if (!this.$refs.termTitle) return

        const termPositionRight = this.$refs.termTitle.getBoundingClientRect()
          .right

        const containerPositionRight = document
          .querySelector(`[data-ref="${REFS.CONTENT_BODY}"]`)
          .getBoundingClientRect().right

        const wrapperPositionRight = document
          .querySelector(`[data-ref="${REFS.CONTENT_WRAPPER}"]`)
          .getBoundingClientRect().right

        const availableSpace =
          wrapperPositionRight - containerPositionRight - POPUP_SETTINGS.MARGIN
        const popupWidth =
          availableSpace >= POPUP_SETTINGS.MIN_WIDTH
            ? availableSpace
            : POPUP_SETTINGS.MIN_WIDTH
        const positionRight = wrapperPositionRight - termPositionRight

        return {
          width: `${popupWidth}px`,
          right: `-${positionRight}px`,
          zIndex: Z_INDEX.VISIBLE
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.term__wrapper {
  position: relative;
  display: inline-block;

  .term__term-title-container {
    display: inline-block;
  }

  a.term__term-title,
  span.term__term-title {
    display: inline-block;
    padding: 0 3px;
    color: $c--gray-main;
    background: rgba($c--main, 0.2);
    border: 1px solid $c--main;
    border-radius: 3px;
    white-space: nowrap;
  }

  .term__pop-up-wrapper {
    @include tablet {
      z-index: 1000;
      position: fixed;
      width: 100vw;
      height: 100%;
      top: 0;
      left: 0;
      background: rgba($c--black, 0.5);
    }
  }

  .term__pop-up {
    position: absolute;
    top: 0;
    padding: 35px 20px 20px 20px;
    border-radius: 5px;
    border: 1px solid $c--main;
    box-shadow: 0 0 9px 0 rgba($c--black, 0.2);
    background: $c--white;

    @include tablet {
      width: 100vw;
      top: auto;
      bottom: 0;
      left: 0;
      height: fit-content;
    }
  }

  .term__close-icon {
    position: absolute;
    top: 15px;
    right: 15px;
  }

  .term__description {
    position: relative;
    font-size: 14px;
    line-height: 20px;

    @include mobile {
      line-height: 29px;
    }
  }

  .term__link {
    color: $c--main;
    font-weight: 600;

    &:hover {
      color: $c--gray-main;
    }
  }
}
</style>
